import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Subject, of } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';
import { timer } from 'rxjs/internal/observable/timer';
import { catchError, debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { map } from 'rxjs/operators/map';
import { matSpinnerOptions } from '../authentication/authenticator-modal/authenticator-modal.component';
import { safeJsonParse } from '../core/api/general.util';
import { noSort } from '../core/api/model-utils';
import { formatDateAsAWSDateString } from '../shared/patient-form/patient-form.model';
import { PatientPortalErrorStateMatcher } from './patient-portal-error-state-matcher';
import { PatientPortalTypes } from './patient-portal-models/patient-portal.model';
import { HTTPSTATUS, patientPortalDescription, patientPortalFormGroupSchema } from './patient-portal-schema';
import { PatientPortalService } from './patient-portal.service';

@Component({
  selector: 'csi-patient-portal',
  templateUrl: './patient-portal.component.html',
  styleUrls: ['./patient-portal.component.scss'],
  providers: [{ provide: ErrorStateMatcher, useClass: PatientPortalErrorStateMatcher }]
})
export class PatientPortalComponent implements OnInit, OnDestroy {
  public _stepper: MatStepper;
  private countdownSub: Subscription;
  public resendAccessCode = new Subject<any>();
  private destroyed$ = new Subject<void>();
  public remainingTime: string;
  public verifyButtonLabel: string = 'Verify & Access Documents';
  public resendOTPButtonLabel: string = 'Resend OTP';
  @ViewChild('stepper', { static: false }) set stepper(content: MatStepper) {
    if (content) {
      setTimeout(() => {
        this._stepper = content;
        this.updateButtonLabel();
        content.selectionChange.pipe(takeUntil(this.destroyed$)).subscribe(() => {
          this.hasAttemptedSubmit = false;
          this.updateButtonLabel();
        });
      })

    }
  }
  @Input() patientPortal: PatientPortalTypes = 'patient-portal-download';

  public matSpinnerOptions = { ...matSpinnerOptions };
  public showUrl: boolean = false;
  public showAccessCodeTimer: boolean = false;
  public linkRevoked: boolean = false;
  public authenticationErrorMessage: string = null;
  public hasAttemptedSubmit: boolean = false;
  public readonly defaultAuthenticationErrorMessage =
    'We could not verify that information. Please check your details and try again.';
  public readonly stepVerificationErrorMessages: { [stepKey: string]: string } = {
    patientInformation:
      'That postal code and date of birth do not match what the clinic has on file. Check both and try again.',
    patientContactInfo:
      'That email address does not match the one you gave the clinic. Check it and try again.',
    patientAccessCode:
      'That access code is not correct. Check your email for the latest code and try again.'
  };
  public stepDescription = patientPortalDescription;
  public stepButtonLabels: { [stepKey: string]: string } = {
    patientInformation: 'Next',
    patientContactInfo: 'Send Access Code',
    patientAccessCode: 'Verify & Access Documents'
  };
  public showButton: boolean = false;
  constructor(private router: Router, private route: ActivatedRoute, private patientPortalService: PatientPortalService, private translocoService: TranslocoService
  ) {

  }
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public todaysDate: Date = new Date();
  public dateToday: Date = new Date();


  public formGroup = new FormGroup({
    accessCode: new FormControl({ value: null, disabled: true }),
    date: new FormControl(null),
    postal: new FormControl(null),
    email: new FormControl({ value: null, disabled: true })

  });


  public needAccesCode: boolean = false;
  public patientPortalFormGroup: FormGroup = new FormGroup({});


  public patientFormGroupSchema = patientPortalFormGroupSchema;


  ngOnInit() {
    const stepKeys = Object.keys(this.stepButtonLabels);
    const labelsToTranslate = [
      ...stepKeys.map(stepKey => this.stepButtonLabels[stepKey]),
      this.resendOTPButtonLabel
    ];
    this.translocoService.selectTranslate(labelsToTranslate).pipe(
      takeUntil(this.destroyed$)).subscribe((translatedLabels => {
        stepKeys.forEach((stepKey, index) => {
          this.stepButtonLabels[stepKey] = translatedLabels[index];
        });
        this.resendOTPButtonLabel = translatedLabels[stepKeys.length];
        this.verifyButtonLabel = this.stepButtonLabels.patientAccessCode;
        this.updateButtonLabel();
      }))
    this.todaysDate.setDate(this.dateToday.getDate());
    this.matSpinnerOptions.text = this.stepButtonLabels.patientInformation;
    this.matSpinnerOptions.active = false;
    this.generateFormGroup();
    this.patientPortalFormGroup.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.authenticationErrorMessage = null;
      });
    this.resendAccessCode.pipe(debounceTime(1000),
      takeUntil(this.destroyed$),
      switchMap(() => {
        this.matSpinnerOptions.active = true;
        this.authenticationErrorMessage = null;
        this.startCounter();
        return this.patientPortalService.authenticatePatient(this.patientPortalService.patientData).pipe(
          catchError(requestError => {
            console.error('Resend access code failed', requestError);
            this.authenticationErrorMessage = this.defaultAuthenticationErrorMessage;
            return of(null);
          })
        )
      })).subscribe(response => {
        this.showButton = false;
        this.matSpinnerOptions.active = false;
        this.updateButtonLabel();
      })
  }

  private generateFormGroup() {
    Object.entries(this.patientFormGroupSchema).forEach(([key, value]) => {
      this.patientPortalFormGroup.addControl(key,
        new FormGroup(
          Object.entries(this.patientFormGroupSchema[key]).reduce((group, [key, formConfig],) =>
            ({ ...group, [key]: new FormControl({ value: null, disabled: formConfig.isDisabled }, formConfig.validator) }), {}),
        )
      )
    })
  }

  private updateButtonLabel(): void {
    if (this.showButton) {
      this.matSpinnerOptions.text = this.resendOTPButtonLabel;
      return;
    }
    const currentStepKey = this._stepper && this._stepper.selected && this._stepper.selected.label;
    if (currentStepKey && this.stepButtonLabels[currentStepKey]) {
      this.matSpinnerOptions.text = this.stepButtonLabels[currentStepKey];
      return;
    }
    this.matSpinnerOptions.text = this.stepButtonLabels.patientInformation;
  }

  private startCounter() {
    const targetDate = new Date();
    targetDate.setMinutes(targetDate.getMinutes() + 4);

    this.countdownSub = timer(0, 1000)
      .pipe(
        map(() => this.getTimeDifference(targetDate))
      ).subscribe(time => {
        this.remainingTime = time;
        if (time === '00:00:00') {
          this.showButton = true;
          this.updateButtonLabel();
          this.countdownSub.unsubscribe();
        }
      })
  }
  
  private buildRequestDataForStep(stepKey: string): { [fieldKey: string]: any } {
    const stepValue = { ...this._stepper.selected.stepControl.value };
    const stepSchema = this.patientFormGroupSchema[stepKey];
    Object.keys(stepSchema).forEach(fieldKey => {
      if (stepSchema[fieldKey].isDateField && stepValue[fieldKey] instanceof Date) {
        stepValue[fieldKey] = formatDateAsAWSDateString(stepValue[fieldKey]);
      }
    });
    return stepValue;
  }

  submit() {
    this.hasAttemptedSubmit = true;

    if (this.patientPortalFormGroup.controls[this._stepper.selected.label].invalid) {

      this.patientPortalFormGroup.markAllAsTouched();
      return;
    }

    this.patientPortalService.patientData = {
      type: this._stepper.selected.label,
      data: {
        patientPortalId: this.patientPortalService.setPatientPortalId,
        ...this.buildRequestDataForStep(this._stepper.selected.label)
      }
    }
    this.matSpinnerOptions.active = true;
    this.authenticationErrorMessage = null;
    this.patientPortalService.authenticatePatient(this.patientPortalService.patientData).subscribe({
      next: (response => {
        if (!response) {
          this.authenticationErrorMessage = this.defaultAuthenticationErrorMessage;
          return;
        }
        const responseMessage = safeJsonParse(response.message);
        switch (response.statusCode) {
          case HTTPSTATUS.OK:
            if (!responseMessage || !responseMessage.nextKey) {
              this.authenticationErrorMessage = this.defaultAuthenticationErrorMessage;
              return;
            }
            if (responseMessage.nextKey === 'patientAccessCode') {
              this.startCounter();
              this.showAccessCodeTimer = true;
            }
            this.patientPortalFormGroup.controls[responseMessage.nextKey].enable();
            this._stepper.next();
            return;

          case HTTPSTATUS.ERROR:
            this.authenticationErrorMessage = this.resolveErrorMessage(responseMessage);
            return;

          case HTTPSTATUS.MAXIMUM:
            this.showAccessCodeTimer = false;
            this.linkRevoked = true;
            this.matSpinnerOptions.disabled = true;
            return;

          case HTTPSTATUS.REDIRECT:
            this.patientPortalService._savedPdfLink = responseMessage && responseMessage.url;
            this.patientPortalService.patientInformation = responseMessage && responseMessage.patient;
            if (!this.patientPortalService._savedPdfLink) {
              this.authenticationErrorMessage = this.defaultAuthenticationErrorMessage;
              return;
            }
            this.router.navigate([`patient-portal/${this.patientPortalService.setPatientPortalId}`]);
            return;

          default:
            this.authenticationErrorMessage = this.resolveErrorMessage(responseMessage);
            return;
        }

      }),
      error: (requestError => {
        console.error('Patient portal authentication failed', requestError);
        this.matSpinnerOptions.active = false;
        this.authenticationErrorMessage = this.defaultAuthenticationErrorMessage;
      }),
      complete: (() => {
        this.matSpinnerOptions.active = false;
      })
    })
  }

  private resolveErrorMessage(responseMessage: any): string {
    if (typeof responseMessage === 'string' && responseMessage) {
      return responseMessage;
    }
    if (responseMessage && typeof responseMessage.message === 'string' && responseMessage.message) {
      return responseMessage.message;
    }
    return this.currentStepVerificationErrorMessage;
  }

  private get currentStepVerificationErrorMessage(): string {
    const currentStepKey = this._stepper && this._stepper.selected && this._stepper.selected.label;
    if (currentStepKey && this.stepVerificationErrorMessages[currentStepKey]) {
      return this.stepVerificationErrorMessages[currentStepKey];
    }
    return this.defaultAuthenticationErrorMessage;
  }



  noSort = noSort;

  private getTimeDifference(targetDate: Date): string {
    const difference = targetDate.getTime() - new Date().getTime();
    if (difference <= 0) return '00:00:00';

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    // Format to ensure two digits for consistent display
    const formatUnit = (unit: number) => unit < 10 ? `0${unit}` : unit.toString();

    if (days > 0) {
      return `${days}d ${formatUnit(hours)}:${formatUnit(minutes)}:${formatUnit(seconds)}`;
    }
    return `${formatUnit(hours)}:${formatUnit(minutes)}:${formatUnit(seconds)}`;
  }
}
